/**
 * ToolCallCard — inline card that renders one tool invocation.
 *
 * Sprint AI-MODULE v2 AI-19; v2.3 UX-1 collapses the card by default
 * once the tool finishes so the assistant response reads cleanly. Open
 * while running, auto-collapses on completion, click the header to
 * re-expand.
 */
import { useState, type FC } from 'react';
import { CheckCircle2, AlertCircle, ChevronDown, ChevronRight, Loader2, Wrench } from 'lucide-react';

interface Props {
  slug: string;
  input: Record<string, unknown>;
  done?: boolean;
  isError?: boolean;
  citationCount?: number;
}

const ToolCallCard: FC<Props> = ({ slug, input, done, isError, citationCount }) => {
  const status = !done ? 'running' : isError ? 'error' : 'ok';
  // Default open while streaming; collapse once done unless the user
  // has already toggled it. `wasOpenedByUser` sticks so re-renders
  // (e.g. citationCount arriving) don't force it closed again.
  const [open, setOpen] = useState<boolean | null>(null);
  const isOpen = open === null ? !done : open;
  return (
    <div className={`ai-tool-card ai-tool-card--${status}`}>
      <button
        type="button"
        className="ai-tool-card__head"
        onClick={() => setOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        <Wrench size={12} />
        <span className="ai-tool-card__slug">{slug}</span>
        {status === 'running' && <Loader2 size={12} className="spin" />}
        {status === 'ok' && <CheckCircle2 size={12} className="ai-tool-card__ok" />}
        {status === 'error' && <AlertCircle size={12} className="ai-tool-card__err" />}
        {!isOpen && status === 'ok' && typeof citationCount === 'number' && (
          <span className="ai-tool-card__foot-inline">
            → {citationCount} citation{citationCount === 1 ? '' : 's'}
          </span>
        )}
      </button>
      {isOpen && (
        <>
          <pre className="ai-tool-card__input">{JSON.stringify(input, null, 0)}</pre>
          {status === 'ok' && typeof citationCount === 'number' && (
            <div className="ai-tool-card__foot">
              → {citationCount} citation{citationCount === 1 ? '' : 's'}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ToolCallCard;
